import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import theme from 'src/utils/styles/theme'

import { useStaticQuery, graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next'
import moment from 'moment-timezone'

import {
  CountdownSection,
  CountdownSectionContainer,
  CountDownContainer,
  Header,
  SubHeader,
  Time,
  ColonWrapper,
  Label,
  BuyNowContainer,
  BuyNowLink,
  BuyNowText,
  Col,
  TextContainer,
  HideOnMobile,
  HideOnDesktop,
} from './styles'

import { BUY_LINK, CAL_BUY_LINK, LOCALIZATION, SEASONAL_STATES } from '../../utils/constants'

const SeasonalCountDownTimer = ({
  holidayDate,
  onCountdownComplete,
  holidayState,
  marginTop,
  frame,
  frame2,
}) => {
  const {
    bfcmCoreBkgd,
    bfcmCoreBkgdMobile,
    bfcmLastChanceBkgd,
    bfcmLastChanceBkgdMobile,
    bfcmVipBkgd,
    bfcmVipBkgdMobile,
  } = useStaticQuery(graphql`
    query SeasonalCountDownTimerQuery {
      bfcmCoreBkgd: file(relativePath: { eq: "bfcm2025/countdown_bkgd_core_desk.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      bfcmCoreBkgdMobile: file(relativePath: { eq: "bfcm2025/countdown_bkgd_core_mobile.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      bfcmLastChanceBkgd: file(
        relativePath: { eq: "bfcm2025/countdown_bkgd_lastChance_desk.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      bfcmLastChanceBkgdMobile: file(
        relativePath: { eq: "bfcm2025/countdown_bkgd_lastChance_mobile.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      bfcmVipBkgd: file(relativePath: { eq: "bfcm2025/countdown_bkgd_vip_desk.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      bfcmVipBkgdMobile: file(relativePath: { eq: "bfcm2025/countdown_bkgd_vip_mobile.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
    }
  `)

  const { t } = useTranslation('common')

  const [showDays, setShowDays] = useState('00')
  const [showHours, setShowHours] = useState('00')
  const [showMinutes, setShowMinutes] = useState('00')
  const [showSeconds, setShowSeconds] = useState('00')
  const [countdownCompleted, setCountdownCompleted] = useState(false)

  const convertToDoubleDigitString = (integer) => {
    if (integer > 9) return integer.toString()
    if (integer < 0) return `00`
    return `0${integer}`
  }

  const timeZones = {
    en: 'America/Los_Angeles', // PT
    'en-ca': 'America/New_York', // ET
    'en-au': 'Australia/Sydney', // AEDT
    'en-gb': 'Europe/London', // BST
  }

  const targetTimeZone = timeZones[LOCALIZATION] || 'America/Los_Angeles' // Get the target timezone based on localization
  const countDownDate = moment.tz(holidayDate, targetTimeZone).valueOf() // Convert holiday date to target timezone

  useEffect(() => {
    const now = moment().valueOf()

    if (countDownDate - now <= 0) {
      setCountdownCompleted(true)
      onCountdownComplete()
    }
  }, [countDownDate, onCountdownComplete])

  useEffect(() => {
    const x = setInterval(() => {
      const now = moment().valueOf()
      const distance = countDownDate - now
      if (distance <= 0) {
        clearInterval(x)
        setCountdownCompleted(true)
        setShowDays('00')
        setShowHours('00')
        setShowMinutes('00')
        setShowSeconds('00')

        if (onCountdownComplete) {
          onCountdownComplete() // Notify that the countdown is complete
        }
      } else {
        const days = convertToDoubleDigitString(Math.floor(distance / (1000 * 60 * 60 * 24)))
        const hours = convertToDoubleDigitString(
          Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        )
        const minutes = convertToDoubleDigitString(
          Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
        )
        const seconds = convertToDoubleDigitString(Math.floor((distance % (1000 * 60)) / 1000))
        setShowDays(days)
        setShowHours(hours)
        setShowMinutes(minutes)
        setShowSeconds(seconds)
      }
    }, 1000)

    return () => clearInterval(x)
  }, [countDownDate, onCountdownComplete])

  if (countdownCompleted) {
    return null
  }

  const timeDisplay = [
    { content: showDays, label: 'DAYS' },
    { content: showHours, label: 'HRS' },
    { content: showMinutes, label: 'MINS' },
    { content: showSeconds, label: 'SECS' },
  ]
  if (countdownCompleted) {
    return null
  }

  const { bfcmCore, vip, bfcmLastChance } = SEASONAL_STATES
  const holidayStates = {
    [bfcmCore]: {
      headerText: 'The Black Friday Sale is Here',
      headerColor: theme.mint,
      subHeaderText: `It's our BIGGEST sale of the year!`,
      subHeaderColor: theme.white,
      ctaText: 'Shop Now',
      ctaTextColor: theme.space,
      ctaTextColorMobile: theme.mint,
      ctaBgColor: theme.mint,
      ctaLink: frame ? BUY_LINK : CAL_BUY_LINK,
      labelColor: theme.white,
      timeColor: theme.white,
      bckgndDesk: bfcmCoreBkgd.childImageSharp.gatsbyImageData,
      bckgndMobile: bfcmCoreBkgdMobile.childImageSharp.gatsbyImageData,
    },
    [vip]: {
      headerText: 'VIP Early Access is On',
      headerColor: theme.mistletoe,
      subHeaderText: `Jump the line & secure the BIGGEST deals of the year before the holiday rush.`,
      subHeaderColor: theme.white,
      ctaText: 'Shop Now',
      ctaTextColor: theme.evergreen,
      ctaTextColorMobile: theme.frost,
      ctaBgColor: theme.frost,
      ctaLink: frame ? BUY_LINK : CAL_BUY_LINK,
      labelColor: theme.white,
      timeColor: theme.white,
      bckgndDesk: bfcmVipBkgd.childImageSharp.gatsbyImageData,
      bckgndMobile: bfcmVipBkgdMobile.childImageSharp.gatsbyImageData,
    },
    [bfcmLastChance]: {
      headerText: `Time's Running Out`,
      headerColor: theme.lightBlush,
      subHeaderText: `It's your last chance to save BIG on our most loved Calendars & Frames!`,
      subHeaderColor: theme.white,
      ctaText: 'Shop Now',
      ctaTextColor: theme.darkBlush,
      ctaTextColorMobile: theme.lightBlush,
      ctaBgColor: theme.blush,
      ctaLink: frame ? BUY_LINK : CAL_BUY_LINK,
      labelColor: theme.white,
      timeColor: theme.white,
      bckgndDesk: bfcmLastChanceBkgd.childImageSharp.gatsbyImageData,
      bckgndMobile: bfcmLastChanceBkgdMobile.childImageSharp.gatsbyImageData,
    },
  }

  const {
    headerText,
    subHeaderText,
    bckgndDesk,
    bckgndMobile,
    ctaText,
    ctaLink,
    headerColor,
    subHeaderColor,
    ctaTextColor,
    ctaTextColorMobile,
    ctaBgColor,
    labelColor,
    timeColor,
  } = holidayStates[holidayState] || holidayStates.bfcmCore

  return (
    <CountdownSection marginTop={marginTop} frame2={frame2} frame={frame}>
      <HideOnMobile>
        <GatsbyImage className="countdown-background-image" image={bckgndDesk} alt="" />
      </HideOnMobile>
      <HideOnDesktop>
        <GatsbyImage className="countdown-background-image" image={bckgndMobile} alt="" />
      </HideOnDesktop>
      <CountdownSectionContainer>
        <TextContainer>
          <Header headerColor={headerColor}>{t(headerText)}</Header>
          <SubHeader subHeaderColor={subHeaderColor}>{t(subHeaderText)}</SubHeader>
        </TextContainer>
        <CountDownContainer>
          {timeDisplay.map((time, i) => (
            <>
              <Col>
                <Time timeColor={timeColor}>{time.content}</Time>
                <Label labelColor={labelColor}>
                  <Trans>{time.label}</Trans>
                </Label>
              </Col>
              {i < timeDisplay.length - 1 && (
                <ColonWrapper>
                  <Time timeColor={timeColor}>:</Time>
                </ColonWrapper>
              )}
            </>
          ))}
        </CountDownContainer>
        <BuyNowContainer>
          <BuyNowLink ctaBgColor={ctaBgColor} to={ctaLink}>
            <BuyNowText ctaTextColor={ctaTextColor} ctaTextColorMobile={ctaTextColorMobile}>
              {t(ctaText)}
            </BuyNowText>
          </BuyNowLink>
        </BuyNowContainer>
      </CountdownSectionContainer>
    </CountdownSection>
  )
}

SeasonalCountDownTimer.propTypes = {
  holidayDate: PropTypes.string.isRequired,
  onCountdownComplete: PropTypes.func.isRequired,
  holidayState: PropTypes.oneOf(['vip', 'bfcmCore', 'bfcmLastChance']).isRequired,
  marginTop: PropTypes.bool,
  frame: PropTypes.bool,
  frame2: PropTypes.bool,
}

export default SeasonalCountDownTimer
